import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import LoginLandingPage from './pages/LoginLandingPage';
import RoleLoginPage from './pages/RoleLoginPage';
import ProfilePage from './pages/ProfilePage';
import LearnerDashboard from './pages/LearnerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ContentManagerDashboard from './pages/ContentManagerDashboard';
import CourseDetailPage from './pages/CourseDetailPage';
import CourseEditorPage from './pages/CourseEditorPage';
import NotFoundPage from './pages/NotFoundPage';
import CertVerifyPage from './pages/CertVerifyPage';
import AuthCallbackPage from './pages/AuthCallbackPage';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    background: { default: '#f5f5f5' },
  },
  typography: {
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
  },
});

const RoleBasedDashboard: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <AdminDashboard />;
  if (user?.role === 'content_manager') return <ContentManagerDashboard />;
  return <LearnerDashboard />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Navbar />
          <Routes>
            <Route path="/login" element={<LoginLandingPage />} />
            <Route path="/login/:role" element={<RoleLoginPage />} />

            <Route path="/" element={
              <ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>
            } />

            <Route path="/dashboard" element={
              <ProtectedRoute><RoleBasedDashboard /></ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute><ProfilePage /></ProtectedRoute>
            } />

            <Route path="/courses/:courseId" element={
              <ProtectedRoute><CourseDetailPage /></ProtectedRoute>
            } />

            <Route path="/admin" element={
              <ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>
            } />

            <Route path="/content" element={
              <ProtectedRoute requiredRole={['content_manager', 'admin']}>
                <ContentManagerDashboard />
              </ProtectedRoute>
            } />

            <Route path="/content/editor/:courseId" element={
              <ProtectedRoute requiredRole={['content_manager', 'admin']}>
                <CourseEditorPage />
              </ProtectedRoute>
            } />

            {/* Public certificate verification — no auth required */}
            <Route path="/verify/:code" element={<CertVerifyPage />} />

            {/* OAuth callback — no auth required */}
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
